/**
 * @file MultiTenantSSOWizard.js
 * @description Component for managing the Multi-tenant SSO Wizard with multiple tabs.
 */

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import FormatTab from './tabs/FormatTab';
import AttributesTab from './tabs/AttributesTab';
import CertificatesTab from './tabs/CertificatesTab';
import ClientsTab from './tabs/ClientsTab';
import {
    fetchSystemSSOById,
    createSystemSSO,
    updateSystemSSO,
    fetchSystemSSOClients,
} from '../../services/ssoService';
import styles from './sso.module.scss';
import Toast from '../ui/toasts/Toast';
import { computeGlobalSummary } from '../../services/ssoSummaryService';
import { downloadCSV, hasText } from '../../utils/utils';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { useAuthContext } from '../../store/Contexts';
import { SecurityAttributes } from '../../utils/data/enums';

import { ROUTES } from '../../constants/routes';
import { getText } from '../../i18n';
const TABS = {
    FORMAT: 'Format',
    ATTRIBUTES: 'Attributes',
    CERTIFICATES: 'Certificates',
    CLIENTS: 'Clients',
};

const MultiTenantSSOWizard = () => {
    const navigate = useNavigate();
    const { showModal } = useContext(ModalContext);
    const { ssoMultiId } = useParams();
    const [activeTab, setActiveTab] = useState(TABS.FORMAT);
    const [ssoData, setSSOData] = useState({});

    const initialFormatTabData = {
        name: '',
        vendorId: null,
        ssoInitiationTypeId: '',
        ssoVendorEnvironmentId: '',
        issuer: '',
        recipient: '',
        audience: '',
        signAssertion: false,
        signResponse: false,
        useRelayState: true,
        useAuthnStatement: false,
        reauthenticate: false,
        url: '',
        partnerURL: '',
        isUrlLocked: false,
    };

    const initialAttributesTabData = {
        nameId: '',
        nameIdFormat: 'unspecified',
        literal: '',
        ssoAttributes: [],
    };

    const initialCertificatesTabData = {
        signingCertificateId: '',
        encryptionCertificateId: null,
        publicKey: '',
        certificate: '',
    };
    const [formatTabData, setFormatTabData] = useState(initialFormatTabData);
    const [attributesTabData, setAttributesTabData] = useState(
        initialAttributesTabData,
    );
    const [certificatesTabData, setCertificatesTabData] = useState(
        initialCertificatesTabData,
    );

    const [originalData, setOriginalData] = useState({
        ...initialFormatTabData,
        ...initialAttributesTabData,
        ...initialCertificatesTabData,
    });

    const [clientsTabData, setClientsTabData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasClients, setHasClients] = useState(false);

    const isEditing = !!ssoMultiId;

    const { hasAttribute } = useAuthContext();
    const canEditForm = hasAttribute(SecurityAttributes.GlobalSSOFullAccess);

    const tabs = isEditing
        ? [TABS.FORMAT, TABS.ATTRIBUTES, TABS.CERTIFICATES, TABS.CLIENTS]
        : [TABS.FORMAT, TABS.ATTRIBUTES, TABS.CERTIFICATES];

    const handleDownloadGlobalSummary = async () => {
        try {
            setIsLoading(true);
            const clients = await fetchSystemSSOClients(ssoMultiId);
            const summaryData = await computeGlobalSummary(ssoData, true);
            const summaryWithClients = summaryData.map(row => ({
                ...row,
                clientCount: clients.length,
            }));
            downloadCSV(
                summaryWithClients,
                `global_summary_${ssoData.name}.csv`,
            );
        } catch (error) {
            console.error('Error downloading global summary:', error);
            Toast.error(getText('msg_7188a4c9a94d'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (ssoMultiId) {
            loadSSOData(ssoMultiId);
        }
    }, [ssoMultiId]);

    const checkUnsavedChanges = () => {
        const currentData = {
            ...formatTabData,
            ...attributesTabData,
            signingCertificateId: certificatesTabData.signingCertificateId,
            encryptionCertificateId:
                certificatesTabData.encryptionCertificateId,
        };

        return Object.keys(currentData).some(
            key =>
                JSON.stringify(currentData[key]) !==
                JSON.stringify(originalData[key]),
        );
    };

    const loadSSOData = async ssoId => {
        try {
            setIsLoading(true);
            const fetchedSSO = await fetchSystemSSOById(ssoId);
            setSSOData(fetchedSSO);

            const tabData = {
                name: fetchedSSO.name || '',
                vendorId: fetchedSSO.vendorId || null,
                ssoInitiationTypeId: fetchedSSO.ssoInitiationTypeId || '',
                ssoVendorEnvironmentId: fetchedSSO.ssoVendorEnvironmentId || '',
                issuer: fetchedSSO.issuer || '',
                recipient: fetchedSSO.recipient || '',
                audience: fetchedSSO.audience || '',
                signAssertion: fetchedSSO.signAssertion || false,
                signResponse: fetchedSSO.signResponse || false,
                useRelayState: fetchedSSO.useRelayState ?? true,
                useAuthnStatement: fetchedSSO.useAuthnStatement || false,
                reauthenticate: fetchedSSO.reauthenticate || false,
                url: fetchedSSO.url || '',
                urlIsLocked: fetchedSSO.urlIsLocked || false,
                partnerURL: fetchedSSO.partnerURL || '',
            };

            const attributesTabData = {
                nameId: fetchedSSO.nameId || '',
                nameIdFormat: fetchedSSO.nameIdFormat || 'unspecified',
                literal: fetchedSSO.literal || '',
                ssoAttributes:
                    fetchedSSO.ssoAttributes.map(e => {
                        return { ...e, isEdited: false };
                    }) || [],
            };

            const certificatesTabData = {
                signingCertificateId: fetchedSSO.signingCertificateId || '',
                encryptionCertificateId:
                    fetchedSSO.encryptionCertificateId || null,
            };

            setOriginalData({
                ...tabData,
                ...attributesTabData,
                ...certificatesTabData,
            });

            setFormatTabData(tabData);
            setAttributesTabData(attributesTabData);
            setCertificatesTabData(certificatesTabData);
        } catch (error) {
            console.error('Failed to fetch SSO data', error);
            Toast.error(getText('msg_fc6bf2c49a8b'));
        } finally {
            setIsLoading(false);
        }
    };

    const validateFormatTab = () => {
        const requiredFields = [
            'name',
            'ssoInitiationTypeId',
            'ssoVendorEnvironmentId',
            'issuer',
            'recipient',
        ];

        for (const field of requiredFields) {
            if (!formatTabData[field]) {
                Toast.error(
                    `${field.charAt(0).toUpperCase() + field.slice(1)} is required`,
                );
                return false;
            }
        }

        if (!hasText(formatTabData.url)) {
            Toast.error('URL is required');
            return false;
        }

        return true;
    };

    const validateAttributeTab = () => {
        if (!attributesTabData.nameId && !attributesTabData.literal) {
            Toast.error(getText('msg_d7e09325f333'));
            return false;
        }

        if (attributesTabData.ssoAttributes.length === 0) {
            Toast.error(getText('msg_651b2f781c2e'));
            return false;
        }

        for (const [index, attr] of attributesTabData.ssoAttributes.entries()) {
            const requiredFields = ['attribute'];
            for (const field of requiredFields) {
                if (!attr[field]) {
                    Toast.error(
                        `${
                            field.charAt(0).toUpperCase() + field.slice(1)
                        } is required for item ${index + 1}`,
                    );
                    return false;
                }
            }
        }

        return true;
    };

    const validateCertificatesTab = () => {
        const requiredFields = ['signingCertificateId'];

        for (const field of requiredFields) {
            if (!certificatesTabData[field]) {
                Toast.error(
                    `${
                        field === 'signingCertificateId'
                            ? 'Signing Certificate'
                            : 'Encryption Certificate'
                    } is required`,
                );
                return false;
            }
        }

        if (
            certificatesTabData.signingCertificate &&
            !certificatesTabData.signingCertificate.value
        ) {
            Toast.error(getText('msg_fea13a3c644f'));
            return false;
        }

        if (
            certificatesTabData.encryptionCertificate &&
            !certificatesTabData.encryptionCertificate.value
        ) {
            Toast.error(getText('msg_2f1ed5555055'));
            return false;
        }

        return true;
    };

    const validateTab = tab => {
        // TODO: VALIDATIONS
        switch (tab) {
            case TABS.FORMAT:
                return validateFormatTab();
            case TABS.ATTRIBUTES:
                return validateAttributeTab();
            case TABS.CERTIFICATES:
                return validateCertificatesTab();
            case TABS.CLIENTS:
                // Add validation for other tabs if needed
                return true;
            default:
                return true;
        }
    };

    const updateSSOData = (tab, data) => {
        switch (tab) {
            case TABS.FORMAT:
                setSSOData({ ...ssoData, ...data });
                break;
            case TABS.ATTRIBUTES:
                setSSOData({ ...ssoData, ...data });
                break;
            case TABS.CERTIFICATES:
                setSSOData({ ...ssoData, ...data });
                break;
            default:
                break;
        }
    };

    const handleNext = () => {
        if (!validateTab(activeTab)) {
            return;
        }

        updateSSOData(activeTab, getActiveTabData());

        const currentTabIndex = tabs.indexOf(activeTab);
        if (currentTabIndex < tabs.length - 1) {
            setActiveTab(tabs[currentTabIndex + 1]);
        } else {
            handleSave();
        }
    };

    const handleTabChange = tab => {
        const currentTabIndex = tabs.indexOf(activeTab);
        const clickedTabIndex = tabs.indexOf(tab);

        if (clickedTabIndex > currentTabIndex) {
            for (let i = currentTabIndex; i < clickedTabIndex; i++) {
                const tabToValidate = tabs[i];
                if (!validateTab(tabToValidate)) {
                    return;
                }
            }
        }

        updateSSOData(activeTab, getActiveTabData());
        setActiveTab(tab);
    };

    const getActiveTabData = () => {
        switch (activeTab) {
            case TABS.FORMAT:
                return formatTabData;
            case TABS.ATTRIBUTES:
                return attributesTabData;
            case TABS.CERTIFICATES:
                return certificatesTabData;
            case TABS.CLIENTS:
                return clientsTabData;
            default:
                return {};
        }
    };

    const handleBack = () => {
        const currentTabIndex = Object.values(TABS).indexOf(activeTab);
        if (currentTabIndex > 0) {
            const previousTab = Object.values(TABS)[currentTabIndex - 1];
            setActiveTab(previousTab);
        }
    };

    const handleSave = async () => {
        if (!validateTab(activeTab)) {
            return true;
        }
        try {
            setIsLoading(true);
            const combinedSSOData = {
                ...ssoData,
                id: ssoMultiId ? parseInt(ssoMultiId) : 0,
                name: formatTabData.name,
                vendorId: formatTabData.vendorId,
                ssoInitiationTypeId: formatTabData.ssoInitiationTypeId,
                ssoVendorEnvironmentId: formatTabData.ssoVendorEnvironmentId,
                issuer: formatTabData.issuer,
                recipient: formatTabData.recipient,
                audience: formatTabData.audience,
                signAssertion: formatTabData.signAssertion,
                signResponse: formatTabData.signResponse,
                useRelayState: formatTabData.useRelayState,
                useAuthnStatement: formatTabData.useAuthnStatement,
                reauthenticate: formatTabData.reauthenticate,
                signingCertificateId: certificatesTabData.signingCertificateId,
                encryptionCertificateId:
                    certificatesTabData.encryptionCertificateId,
                url: formatTabData.url,
                partnerURL: formatTabData.partnerURL || '',
                nameId: attributesTabData.literal
                    ? null
                    : attributesTabData.nameId,
                nameIdFormat: attributesTabData.nameIdFormat,
                literal: attributesTabData.literal,
                urlIsLocked: formatTabData.urlIsLocked,
                ssoAttributes: attributesTabData.ssoAttributes.map(attr => {
                    const attrData = {
                        ...attr,
                        ssoId: ssoMultiId ? parseInt(ssoMultiId) : 0,
                    };
                    delete attrData.isEdited;
                    return attrData;
                }),
            };

            if (ssoMultiId) {
                await updateSystemSSO(combinedSSOData);
                Toast.success(getText('msg_aa8320dcb015'));
            } else {
                await createSystemSSO(combinedSSOData);
                Toast.success(getText('msg_84cf570b9842'));
            }

            setOriginalData({
                ...formatTabData,
                ...attributesTabData,
                ...certificatesTabData,
            });

            navigate(ROUTES.SSO_LIST);
        } catch (error) {
            console.error('Failed to save SSO', error);
            const { response } = error;
            const errorMessage =
                response?.data?.detail ||
                'There was an issue saving the SSO data.';
            Toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (checkUnsavedChanges()) {
            showModal({
                type: ModalTypes.ACTION,
                title: getText('msg_b78130c92f32'),
                subtitle: getText('msg_2f4e8fc9ac09'),
                text: getText('msg_f3cefc556397'),
                onYes: () => navigate(ROUTES.SSO_LIST),
                onNo: () => {},
                noButtonText: getText('msg_77dfd2135f4d'),
                yesButtonText: getText('msg_36fff63ccbcd'),
            });
        } else {
            navigate(ROUTES.SSO_LIST);
        }
    };

    return (
        <div className={`page-content ${styles.container}`}>
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center border-bottom position-relative pb-3 flex-column flex-xl-row gap-2">
                        <h2 className="page-title float-start ms-4 m-0 col-auto col-lg-2 text-nowrap">
                            {ssoMultiId
                                ? ssoData?.name || 'Edit SSO'
                                : 'Add SSO'}
                        </h2>
                        <ul className={`${styles.tabList} col-auto`}>
                            {tabs.map((tab, index) => (
                                <React.Fragment key={tab}>
                                    <li
                                        className={`mx-2 ${styles.tabItem} ${
                                            activeTab === tab
                                                ? styles.active
                                                : ''
                                        }`}
                                        onClick={() => handleTabChange(tab)}
                                    >
                                        {tab}
                                    </li>
                                    {index < tabs.length - 1 && (
                                        <i className="bi bi-chevron-right mx-2" />
                                    )}
                                </React.Fragment>
                            ))}
                        </ul>
                        {activeTab === TABS.CLIENTS && hasClients ? (
                            <span
                                type="button"
                                onClick={handleDownloadGlobalSummary}
                                className="ing-text-buttons mx-3 float-end text-nowrap"
                            >
                                {getText('msg_4e427fcba695')}
                                <i className="bi bi-cloud-download me-2 mx-1"></i>
                            </span>
                        ) : (
                            <div className="col-auto col-lg-2 d-flex justify-content-end justify-content-lg-evenly">
                                {ssoMultiId && canEditForm && (
                                    <span
                                        type="button"
                                        onClick={handleSave}
                                        className="ing-text-buttons me-3 d-flex"
                                    >
                                        <i className="bi bi-check me-2" />
                                        {getText('msg_efc007a393f6')}
                                    </span>
                                )}
                                <span
                                    type="button"
                                    onClick={handleClose}
                                    className="ing-text-buttons me-3 d-flex"
                                >
                                    <i className="bi bi-x me-2" />
                                    {getText('msg_bbfa773e5a63')}
                                </span>
                            </div>
                        )}
                    </div>

                    {isLoading ? (
                        <div className="w-100 mt-5 pb-5 text-center">
                            {getText('msg_b04ba49f8486')}
                        </div>
                    ) : (
                        <div
                            className={`w-100 mt-5 pb-5 ${styles.tabContainer}`}
                        >
                            <div className={styles.tabContent}>
                                {activeTab === TABS.FORMAT && (
                                    <FormatTab
                                        data={formatTabData}
                                        onChange={setFormatTabData}
                                    />
                                )}
                                {activeTab === TABS.ATTRIBUTES && (
                                    <AttributesTab
                                        data={attributesTabData}
                                        onChange={setAttributesTabData}
                                    />
                                )}
                                {activeTab === TABS.CERTIFICATES && (
                                    <CertificatesTab
                                        data={certificatesTabData}
                                        onChange={setCertificatesTabData}
                                    />
                                )}
                                {activeTab === TABS.CLIENTS && isEditing && (
                                    <ClientsTab
                                        ssoId={ssoMultiId}
                                        onClientsLoaded={setHasClients}
                                    />
                                )}
                            </div>
                            <div className="col-6 d-block mt-4 m-auto position-relative">
                                {activeTab !== TABS.CLIENTS && (
                                    <p>
                                        <span className="text-danger pb-4">
                                            *
                                        </span>{' '}
                                        {getText('msg_7c118f042eaa')}
                                    </p>
                                )}
                                {activeTab !== TABS.FORMAT && (
                                    <button
                                        type="button"
                                        className="float-start button-base m-auto ing-popup-blue-negative"
                                        onClick={handleBack}
                                    >
                                        <i className="bi bi-chevron-left" />{' '}
                                        {getText('msg_b52b36b7269f')}
                                    </button>
                                )}
                                {activeTab !== tabs[tabs.length - 1] ? (
                                    <button
                                        type="button"
                                        className="float-end button-base ing-button-primary-left m-auto"
                                        onClick={handleNext}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Loading...' : 'Next'}{' '}
                                        <i className="bi bi-chevron-right" />
                                    </button>
                                ) : (
                                    canEditForm && (
                                        <button
                                            type="button"
                                            className="float-end button-base ing-button-primary-left m-auto"
                                            onClick={handleSave}
                                            disabled={isLoading}
                                        >
                                            {isLoading
                                                ? 'Saving...'
                                                : isEditing
                                                  ? 'Update'
                                                  : 'Done'}
                                        </button>
                                    )
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MultiTenantSSOWizard;
